import { useState, type FormEvent } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { ChannelRequestedRole } from '@/services/channelApi';

interface RequestToJoinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (message: string, requestedRole: ChannelRequestedRole) => Promise<void>;
  channelName: string;
}

export function RequestToJoinModal({
  isOpen,
  onClose,
  onSubmit,
  channelName,
}: RequestToJoinModalProps) {
  const [message, setMessage] = useState('');
  const [requestedRole, setRequestedRole] = useState<ChannelRequestedRole>('athlete');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmit(message.trim(), requestedRole);
      setMessage('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        style={{ backgroundColor: 'var(--color-dark-overlay)' }}
        className="fixed inset-0"
      />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          transition
          style={{
            backgroundColor: 'var(--color-dark-card)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: '16px',
            boxShadow: 'var(--shadow-modal)',
            width: '100%',
            maxWidth: '440px',
            padding: '28px',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px',
            }}
          >
            <h2
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                fontSize: '17px',
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              Request to Join
            </h2>
            <button
              type="button"
              onClick={onClose}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: 'var(--color-text-muted)',
              }}
            >
              <XMarkIcon style={{ width: '18px', height: '18px' }} />
            </button>
          </div>

          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '20px' }}>
            Send a request to join <strong style={{ color: 'var(--color-text-primary)' }}>{channelName}</strong>.
            The channel owner will review your request.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Role selector */}
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: '6px',
                  }}
                >
                  Requesting role
                </label>
                <select
                  value={requestedRole}
                  onChange={(e) => setRequestedRole(e.target.value as ChannelRequestedRole)}
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: '10px',
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-dark-elevated-2)',
                    color: 'var(--color-text-primary)',
                    fontSize: '14px',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <option value="athlete">Athlete</option>
                  <option value="coach">Coach</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>

              {/* Optional message */}
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'var(--color-text-secondary)',
                    marginBottom: '6px',
                  }}
                >
                  Message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell the coach why you want to join..."
                  rows={3}
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    borderRadius: '10px',
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'var(--color-dark-elevated-2)',
                    color: 'var(--color-text-primary)',
                    padding: '10px 12px',
                    fontSize: '14px',
                    resize: 'vertical',
                    outline: 'none',
                    fontFamily: 'var(--font-body)',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {error && (
                <p role="alert" style={{ fontSize: '13px', color: 'var(--color-danger)' }}>
                  {error}
                </p>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  style={{
                    padding: '9px 20px',
                    borderRadius: '10px',
                    border: '1px solid var(--color-border-subtle)',
                    backgroundColor: 'transparent',
                    color: 'var(--color-text-secondary)',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '9px 24px',
                    borderRadius: '10px',
                    border: 'none',
                    backgroundColor: 'var(--color-accent)',
                    color: 'var(--color-text-inverse)',
                    fontSize: '14px',
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 600,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {isSubmitting && (
                    <span
                      style={{
                        width: '13px',
                        height: '13px',
                        border: '2px solid rgba(0,0,0,0.2)',
                        borderTopColor: 'rgba(0,0,0,0.7)',
                        borderRadius: '50%',
                        display: 'inline-block',
                        animation: 'spin 0.7s linear infinite',
                      }}
                    />
                  )}
                  Send Request
                </button>
              </div>
            </div>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default RequestToJoinModal;
